import {
  flowEdgePortLabels,
  flowNodeOutputPorts,
  type FlowEdgePort,
  type FlowDraft,
  type FlowNode,
  type FlowNodeConfig,
  type FlowNodeType,
} from "./contracts";
import { graphDraft } from "./fixtures";
import { normalizeFlowDraft } from "./schema";

/** Catálogo visual do editor. A semântica continua nos contratos do domínio. */
export const flowNodeTypeLabels: Record<FlowNodeType, string> = {
  start: "Início",
  condition: "Condição",
  random_split: "Teste A/B",
  stage: "Mudar etapa",
  wait: "Aguardar",
  human_task: "Tarefa humana",
  send_message: "Enviar e-mail",
  meeting: "Reunião",
  billing: "Lembrete de mensalidade",
  ai_response: "Assistência de IA",
  set_field_or_tag: "Definir campo/tag",
  call_flow: "Chamar flow",
  won: "Ganho",
  lost: "Perda",
  end: "Fim",
};

export const flowNodeTypeDescriptions: Record<FlowNodeType, string> = {
  start: "Ponto único de entrada do flow.",
  condition: "Escolhe uma saída com a DSL declarativa tipada.",
  random_split: "Distribui o caminho por pesos inteiros determinísticos.",
  stage: "Registra uma intenção de mudança de etapa.",
  wait: "Suspende até o relógio injetado tornar o caminho elegível.",
  human_task: "Bloqueia para uma decisão ou ação humana.",
  send_message: "Representa um e-mail como intenção versionada.",
  meeting: "Registra a intenção de agendar um encontro com os participantes.",
  billing: "Registra a intenção de lembrar a mensalidade nos dias escolhidos.",
  ai_response: "Solicita uma sugestão de IA sem executar envio.",
  set_field_or_tag: "Representa uma alteração de campo, tag ou gatilho.",
  call_flow: "Resolve um flow publicado relacionado sem criar Lead no editor.",
  won: "Desfecho ganho da participação.",
  lost: "Desfecho perdido com motivo e evidência.",
  end: "Finaliza o caminho sem desfecho comercial.",
};

export const flowNodeOutputPortLabels: Record<FlowEdgePort, string> = flowEdgePortLabels;

export function cloneFlowDraft(draft: FlowDraft): FlowDraft {
  return JSON.parse(JSON.stringify(draft)) as FlowDraft;
}

export function editorPosition(index: number): { x: number; y: number } {
  const column = index % 4;
  const row = Math.floor(index / 4);
  return { x: 80 + column * 280, y: 90 + row * 190 };
}

/** Mantém posições editoriais existentes e dá uma posição legível ao fixture. */
export function withEditorPositions(draft: FlowDraft): FlowDraft {
  return {
    ...cloneFlowDraft(draft),
    nodes: draft.nodes.map((node, index) => ({
      ...node,
      position: node.position ?? editorPosition(index),
    })),
  };
}

export function demoGraphDraft(): FlowDraft {
  return withEditorPositions(
    normalizeFlowDraft({
      ...graphDraft(),
      id: "flow-editor-preview",
      name: "Fluxograma de demonstração",
      description: "Exemplo local para revisar caminhos, condições, A/B e intenções.",
    }),
  );
}

export interface DefaultFlowNodeOptions {
  id: string;
  position: { x: number; y: number };
  stageId?: string;
  lossReasonId?: string;
}

export function defaultFlowNode(
  type: FlowNodeType,
  options: DefaultFlowNodeOptions,
): FlowNode {
  const base: Omit<FlowNode, "config"> = {
    id: options.id,
    type,
    label: flowNodeTypeLabels[type],
    position: options.position,
  };
  let config: FlowNodeConfig;
  switch (type) {
    case "start":
      config = { type: "start" };
      break;
    case "condition":
      config = { type: "condition" };
      break;
    case "random_split":
      config = {
        type: "random_split",
        variants: [
          { key: "a", label: "Variante A", weight: 50 },
          { key: "b", label: "Variante B", weight: 50 },
        ],
      };
      break;
    case "stage":
      config = { type: "stage", stageId: options.stageId ?? "stage-not-selected" };
      break;
    case "wait":
      config = { type: "wait", durationMinutes: 1440 };
      break;
    case "human_task":
      config = {
        type: "human_task",
        instruction: "Revisar o Lead antes de seguir.",
        taskActionType: "human_task",
        sensitive: false,
      };
      break;
    case "send_message":
      config = {
        type: "send_message",
        channel: "email",
        templateId: "template-not-selected",
        templateVersionId: "template-version-not-selected",
        templateVersion: 1,
      };
      break;
    case "meeting":
      config = { type: "meeting", participants: "primary_contact" };
      break;
    case "billing":
      // O protótipo abre com três dias antes e o dia do vencimento, nos três canais.
      config = { type: "billing", offsetsDays: [3, 0], channels: ["system", "email", "whatsapp"] };
      break;
    case "ai_response":
      config = {
        type: "ai_response",
        objective: "compose_reply",
        promptVersionId: "prompt-version-not-selected",
        promptVersion: 1,
      };
      break;
    case "set_field_or_tag":
      config = { type: "set_field_or_tag", assignments: [] };
      break;
    case "call_flow":
      config = {
        type: "call_flow",
        target: {
          scope: "other_funnel",
          funnelId: "funnel-not-selected",
          flowId: "flow-not-selected",
          flowVersionId: "flow-version-not-selected",
          flowVersion: 1,
          originPolicy: "continue_origin",
        },
      };
      break;
    case "won":
      config = { type: "won" };
      break;
    case "lost":
      config = {
        type: "lost",
        lossReasonId: options.lossReasonId ?? "loss-reason-not-selected",
        stageId: options.stageId ?? "stage-not-selected",
        evidence: "Descrever a evidência do desfecho.",
      };
      break;
    case "end":
      config = { type: "end", outcomeNote: "Caminho encerrado." };
      break;
  }
  return { ...base, config };
}

export function outputPortsForNode(node: FlowNode): readonly FlowEdgePort[] {
  return flowNodeOutputPorts[node.type];
}
